import { RefObject } from 'react';

import { SubscriptionResponseV2 } from 'src/types/subscription';
import { mixpanelObject } from 'src/utils/analytics';
import { putFacebookEvent } from 'src/utils/analytics/fbpixel/fbCapiHelper';
import {
  MIXPANEL_COUNTER,
  MIXPANEL_UNIQUE_ID
} from 'src/utils/analytics/mixpanel/mixpanelConstants';
import { packToggleEvent } from 'src/utils/analytics/mixpanel/mixpanelEventsHelper';
import { getLocalStorage } from 'src/utils/browserStorageHandler';
import { ActivePromotions } from 'src/utils/commonTypes';
import { CLOUDINARY_IMAGE_URL } from 'src/utils/constants/ApiConstants';
import { GUEST_TOKEN } from 'src/utils/constants/localStorageConstants';
import { META_EVENTS } from 'src/utils/constants/metaPixelConstants';
import { FeatureFlagConstants } from 'src/utils/constants/utilConstants';
import { isFeatureEnabled } from 'src/utils/RemoteConfig';
import { fbqTrack } from 'src/utils/scripts/thirdPartyScripts';
import {
  calculateDisplayPriceNew,
  calculateSellPriceNew,
  getCurrencySymbol,
  trackSelectedPlanMixpanelEvents
} from 'src/utils/SubscriptionHelper/SubscriptionHelper';
import { generateUUID } from 'src/utils/UtilityMethods';

import {
  Title,
  PlanCardWrapper,
  LeftSection,
  RadioCircle,
  Details,
  Logo,
  DeviceInfo,
  RightSection,
  OldPrice,
  CurrentPrice,
  PremiumPlanCardWrapper,
  ActivePlanCardWrapper,
  RadioCircleWrapper,
  BillingIntervalWrapper,
  PromoTagWrapper
} from './style';
import { useAppStore } from '../../../../hooks/useAppStore';
import {
  SUBSCRIPTION_ACTIONS,
  useSubscriptionDispatchV2
} from '../../SubscriptionContext';
import PromoTag from '../BadgeTags/badgetags';
import Feature from '../Feature';

const PlanCard = ({
  data,
  packToggleCounter,
  activePromotions = {},
  planRef,
  isFullWidth = false
}: {
  data: SubscriptionResponseV2.Plan;
  packToggleCounter: { current: number };
  activePromotions: ActivePromotions;
  planRef?: RefObject<HTMLDivElement>;
  isFullWidth?: boolean;
}) => {
  const {
    translations = {},
    subscriptionData,
    isUserLoggedIn,
    usersData,
    remoteConfig
  } = useAppStore();
  const subscriptionStrings = translations.subscription ?? {};
  const { active } = data;
  const {
    price,
    purchaseAllowed,
    title: optionTitle = '',
    titleTk: optionTitleTk = '',
    features = []
  } = data ?? {};
  const { market, currency = 'INR' } = price ?? {};
  const [, featureItem] = features || [];
  const isPremium = data.categoryId === 'Premium4K';
  const currencySymbol = getCurrencySymbol(currency);
  const subTitle1 = `${currencySymbol} ${
    data ? calculateSellPriceNew(data, activePromotions) : null
  }`;
  const subTitle2 = `${currencySymbol} ${
    data ? calculateDisplayPriceNew(data, activePromotions) : null
  }`;

  const dispatch = useSubscriptionDispatchV2();

  const triggerPackToggleEvents = () => {
    mixpanelObject.setProperty(MIXPANEL_COUNTER, packToggleCounter.current);
    mixpanelObject.setProperty(
      MIXPANEL_UNIQUE_ID,
      isUserLoggedIn ? usersData?.id : getLocalStorage(GUEST_TOKEN)
    );
    packToggleEvent();
    trackSelectedPlanMixpanelEvents(data, !!subscriptionData);
  };

  const handlePlanClick = (planCategory: SubscriptionResponseV2.Plan) => () => {
    if (!purchaseAllowed) {
      return;
    }
    triggerPackToggleEvents();
    if (
      isFeatureEnabled(
        remoteConfig,
        FeatureFlagConstants.FEATURE_META_PIXEL_INTEGRATION
      )
    ) {
      const eventId = generateUUID();
      fbqTrack(META_EVENTS.Addtocart, eventId);
      putFacebookEvent(
        META_EVENTS.Addtocart,
        new Date().getTime(),
        window.location.href,
        eventId
      );
    }
    //to-do
    // const plan = planCategory?.options.find((item) => item.active);
    // if (plan) {
    //   gtm.packSelectedEventGTM(transformNewPlanToOldPlan(plan));
    // }
    dispatch({
      type: SUBSCRIPTION_ACTIONS.UPDATE_SELECTED_PLAN,
      payload: { id: planCategory?.id }
    });
  };
  const isCardFullWidth = () => {
    return data?.isDefault || isFullWidth;
  };
  const getDeviceCount = (deviceText: string, noOfDevices: number) => {
    const subscriptionStringsDeviceText = subscriptionStrings[deviceText];
    const deviceTextWithCount = subscriptionStringsDeviceText.replace(
      '{{number_of_screens}}',
      noOfDevices.toString()
    );
    return <span>{deviceTextWithCount}</span>;
  };
  return (
    <PlanCardWrapper
      isFullWidth={isCardFullWidth()}
      ref={planRef}
      onClick={handlePlanClick(data)}
      css={[
        isPremium && PremiumPlanCardWrapper,
        active && ActivePlanCardWrapper
      ]}
      data-test-id={`plan-category${active ? '-active' : ''}`}>
      <LeftSection>
        <RadioCircleWrapper>
          <RadioCircle data-selected={active} />
        </RadioCircleWrapper>
        <Details>
          <Logo>
            <img
              src={`${CLOUDINARY_IMAGE_URL}${
                active || isPremium
                  ? data.selectedPlanImages.desktopSmall
                  : data.unselectedPlanImages.desktopSmall
              }`}
              alt=""
            />

            {isPremium && !active && (
              <PromoTagWrapper>
                <PromoTag
                  height="16px"
                  text={subscriptionStrings['CMS_ALL_ACCESS_BEST_VALUE_TEXT']}
                />
              </PromoTagWrapper>
            )}
          </Logo>
          <Title data-selected={active}>
            {optionTitleTk && subscriptionStrings[optionTitleTk]
              ? subscriptionStrings[optionTitleTk]
              : optionTitle}
          </Title>
          <DeviceInfo active={active} data-selected={active}>
            {featureItem && <Feature item={featureItem} active={active} />}
            {data.deviceCount &&
              featureItem?.text &&
              getDeviceCount(featureItem.text, data.deviceCount)}
            <span>
              •{' '}
              {data?.adEnabled
                ? subscriptionStrings?.CMS_PLAN_LIMITED_AD_TEXT
                : subscriptionStrings?.CMS_PLAN_AD_FREE_TEXT}
            </span>
          </DeviceInfo>
        </Details>
      </LeftSection>
      <RightSection>
        {market && subTitle2 !== subTitle1 && (
          <OldPrice active={active}>{subTitle2}</OldPrice>
        )}
        <CurrentPrice active={active} data-selected={active}>
          {subTitle1}
        </CurrentPrice>
        {data?.price?.billing_interval_unit &&
          data?.price?.billing_interval_amount && (
            <BillingIntervalWrapper>
              {subscriptionStrings?.[data?.price?.billing_interval_unit ?? '']
                ?.replace('{{price}}', data?.price?.billing_interval_amount)
                .replace('{{currency}}', currencySymbol)}
            </BillingIntervalWrapper>
          )}
      </RightSection>
    </PlanCardWrapper>
  );
};

export default PlanCard;
